import { Button } from "@/design-system/Button/Button";
import { type ClubImportSubsection } from "@/events/clubImportMixpanel";
import { sendMessageToExtension } from "@/functions/sendMessageToExtension";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { useEffect } from "react";
import { isMobileSafari } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router";

import { useEscape } from "../../../hooks/useKeyboardShortcut";
import { arrow as arrowIcon } from "../../../icons";
import { setIsCurrentlyImporting } from "../../../redux/globalState/globalStateSlice";
import { AppDispatch } from "../../../redux/store";
import { getUserSelector } from "../../../redux/user/userSlice";
import Spinner from "../../atoms/Spinner/Spinner";
import { useFinishImport } from "./useFinishImport";

type Props = {
  onClickDone: () => void;
  onClickCancel: () => void;
  /** The screen the import was started from; names the Done event. */
  subsection?: ClubImportSubsection;
};

export const NowImporting = ({
  onClickDone,
  onClickCancel,
  subsection,
}: Props) => {
  const { t } = useTranslation(["clubImport", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(getUserSelector);
  useEffect(() => {
    document.body.classList.add("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  });
  // Done's whole sequence — telling the extension, pulling the players across,
  // and the refreshes that follow — lives in the hook.
  const { finish, loading } = useFinishImport(onClickDone, subsection);

  const onCancel = () => {
    dispatch(setIsCurrentlyImporting(false));
    onClickCancel();
    // Best effort: drops the capture and hides the EA-tab overlay on
    // extensions that know the Cancel Request; older ones reset on next Start.
    sendMessageToExtension({ user, cancelRequest: true }).catch(() => {});
  };
  useEscape(onCancel);
  return (
    <div
      // z-100 is the modal layer: this blocks the page until Done, and it is
      // rendered from ModalControllerV2 as well as from inside panels, where
      // z-20 lost to the z-50 sidebars.
      className="left-0 right-0 h-screen fixed top-0 bottom-0 z-100 px-8"
      style={{
        background:
          "linear-gradient(180deg, rgba(0, 0, 0, 0.67) 21.96%, rgba(0, 0, 0, 0.84) 27.63%, rgba(0, 0, 0, 0.84) 66.71%, rgba(0, 0, 0, 0.67) 71.2%)",
        backdropFilter: "blur(6px)",
      }}
    >
      {/*
        One centred column. Previously the buttons sat beside the text on a
        fixed 900px block with the tutorial link absolutely placed at 60%/30%,
        so nothing lined up with anything else at any width.
      */}
      <section className="flex h-full flex-col items-center justify-center gap-y-8 text-center text-white">
        {loading ? (
          <Spinner fontSize={isMobileSafari ? "40px" : "74px"} />
        ) : (
          <>
            <div className="flex max-w-lg flex-col gap-y-2">
              <h2 className="font-oswald text-3xl mobile:text-4xl">
                {t("nowImporting.title")}
              </h2>
              {/* The instruction only works if the last step stands out — it
                  is the one thing the user has to come back and do. */}
              <p className="text-base text-dark-secondary mobile:text-lg">
                <Trans
                  ns="clubImport"
                  i18nKey="nowImporting.instruction"
                  components={{ b: <span className="font-bold text-white" /> }}
                />
              </p>
            </div>

            <div className="flex flex-row gap-x-3">
              <Button variant="secondary" size="large" onClick={onCancel}>
                {t("common:cancel")}
              </Button>
              <Button variant="primary" size="large" onClick={finish}>
                {t("common:done")}
              </Button>
            </div>

            <NavLink
              to="/club-import-tutorial"
              className="flex flex-row items-center gap-x-1 text-sm text-dark-secondary underline underline-offset-2"
              onClick={onCancel}
            >
              <span>{t("nowImporting.tutorialLink")}</span>
              <div className="w-3 h-3 rotate-90">{arrowIcon}</div>
            </NavLink>
          </>
        )}
      </section>
    </div>
  );
};
